<template>
  <div>
    <div v-if="loading" class="loadingContainer" :style="{marginTop}">
      <v-subheader>
        <p>{{ loadingMessage }}</p>
      </v-subheader>
      <v-progress-circular indeterminate :style="{color: loaderColor}"/>
    </div>
    <template v-else-if="allUsers.length > 0">
      <v-layout row v-for="user in allUsers" :key="user.aoid">
        <v-flex xs12 lg8 offset-lg2>
          <v-card class="userCard" :style="{borderColor: cardColors[user.change]}">
            <v-card-text>
              <v-layout row wrap>
                <v-flex xs4 sm2>
                  <v-subheader>Name</v-subheader>
                </v-flex>
                <v-flex xs8 sm4>
                  <div class="data-display">{{ user.name }}</div>
                </v-flex>
                <v-flex xs4 sm2>
                  <v-subheader>Azure ID</v-subheader>
                </v-flex>
                <v-flex xs8 sm4>
                  <div class="data-display">{{ user.aoid }}</div>
                </v-flex>
                <template v-if="user.change === 'create'">
                  <v-flex xs4 sm2>
                    <v-subheader>Group</v-subheader>
                  </v-flex>
                  <v-flex xs8 sm10>
                    <v-select :value="user.group" @input="setUserGroup(user.aoid, $event)"
                              :items="groupSelect"/>
                  </v-flex>
                </template>
              </v-layout>
            </v-card-text>
          </v-card>
        </v-flex>
      </v-layout>
      <template v-if="canDoAdditional">
        <h2>Additional actions</h2>
        <template v-if="canManageMessaging">
          <h3>Messaging service</h3>
          <v-layout row>
            <v-flex xs12>
              <v-checkbox v-model="additionalActions.createMessagingConfigs"
                          label="Create messaging configurations"/>
              <v-checkbox v-model="additionalActions.cleanupMessagingConfigs"
                          label="Clean up messaging configurations"/>
            </v-flex>
          </v-layout>
        </template>
      </template>
      <v-layout row>
        <v-flex xs12>
          <v-btn to="/users">Cancel</v-btn>
          <template v-if="changes.length > 0">
            <v-btn color="success" @click="performUpdates">Update</v-btn>
          </template>
          <template v-if="updates.length > 0">
            <v-btn color="primary" @click="performUpdateExistingUsers">Update Existing Users</v-btn>
          </template>
        </v-flex>
      </v-layout>
    </template>
    <template v-else>
      <v-layout row>
        <v-flex xs12>
          <v-subheader class="noChanges">Azure users are up to date</v-subheader>
        </v-flex>
      </v-layout>
      <v-layout row>
        <v-flex xs12>
          <v-btn to="/users">Back</v-btn>
        </v-flex>
      </v-layout>
    </template>
  </div>
</template>

<script>
  import { mapState, mapActions } from 'vuex';
  import theme from '@/style/variables';
  import OAuthHandler from '@/oauthHandler';

  const suiteOAuthHandler = new OAuthHandler('/oauth/asuite');

  export default {
    data() {
      return {
        marginTop: window.innerHeight / 3 + 'px',
        loading: true,
        loadingMessage: 'Authenticating to GSuite',
        loaderColor: theme['@edval-dark'],
        userChanges: {},
        cardColors: {
          create: '#6ae260',
          delete: '#e83c34',
          update: '#0d12e8'
        },
        additionalActions: {
          createMessagingConfigs: true,
          cleanupMessagingConfigs: false
        }
      };
    },
    computed: {
      changes() {
        const changes = [];
        ['create', 'delete'].forEach((change) => {
          if (this.userChanges[change]) {
            this.userChanges[change].forEach((u) => changes.push({
              aoid: u.aoid,
              name: u.name,
              change,
              group: null
            }));
          }
        });

        return changes;
      },
      allUsers() {
        const changes = [];
        ['create', 'delete', 'update'].forEach((change) => {
          if (this.userChanges[change]) {
            this.userChanges[change].forEach((u) => changes.push({
              aoid: u.aoid,
              name: u.name,
              change,
              group: null
            }));
          }
        });

        return changes;
      },
        updates() {
        const updates = [];
        ['update'].forEach((change) => {
          if (this.userChanges[change]) {
            this.userChanges[change].forEach((u) => updates.push({
              aoid: u.aoid,
              name: u.name,
              change,
              group: null
            }));
          }
        });

        return updates;
      },
      groups() {
        const groups = {};
        this.$store.state.groups.groups.forEach((group) =>
          groups[group.id] = group.name
        );
        return groups;
      },
      groupSelect() {
        return Object.keys(this.groups).map((gid) => {
          return { value: gid, text: this.groups[gid] };
        });
      },
      canManageMessaging() {
        return 'messaging' in this.user.scopes &&
          this.user.scopes.messaging.authorities.indexOf('MANAGE_USER_CONFIGURATIONS') > 1;
      },
      canDoAdditional() {
        return this.canManageMessaging;
      },
      ...mapState('auth', [
        'user'
      ])
    },
    methods: {
      getUsers(params) {
        this.loadingMessage = 'Retrieving users';
        this.loaderColor = theme['@edval-green'];
        this.$services.authenticator.get(`/suite/updateUsers${params}`)
          .then((res) => {
            this.loading = false;
            this.userChanges = res.data.data;
          }).catch(this.$defaultHandlerHttp);
      },
      setUserGroup(aoid, group) {
        this.userChanges.create.filter((u) => u.aoid === aoid)
          .forEach((u) => u.group = group);
      },
      updateUsers() {
        return this.$services.authenticator.post('/suite/updateUsers', this.userChanges);
      },
      updateExistingUsers() {
        return this.$services.authenticator.post('/suite/updateExistingUsers', this.userChanges);
      },
      importMessagingConfig() {
        return this.$services.messaging.post('/config/import', {
          users: [
            ...this.userChanges.existing,
            ...this.userChanges.create
          ].map((u) => ({
            id: u.aoid,
            emailAddress: u.email
          }))
        });
      },
      cleanupMessagingConfig() {
        return this.$services.messaging.post('/config/cleanup', {
          userIds: [
            ...this.userChanges.existing,
            ...this.userChanges.create
          ].map((u) => u.aoid)
        });
      },
      performUpdates() {
        const actions = [this.updateUsers()];
        if (this.additionalActions.createMessagingConfigs) {
          actions.push(this.importMessagingConfig());
        }
        if (this.additionalActions.cleanupMessagingConfigs) {
          actions.push(this.cleanupMessagingConfig());
        }

        Promise.all(actions)
          .catch(this.$defaultHandlerHttp)
          .then(() => this.resetUsers())
          .then(() => this.$router.push('/users'));
      },
      performUpdateExistingUsers() {
        const actions = [this.updateExistingUsers()];

        Promise.all(actions)
          .catch(this.$defaultHandlerHttp)
          .then(() => this.resetUsers())
          .then(() => this.$router.push('/users'));
      },
      ...mapActions('users', {
        resetUsers: 'reset'
      })
    },
    mounted() {
      this.$services.authenticator.get('/suite/login')
        .then((res) => suiteOAuthHandler.initiate(res.data.data))
        .then((params) => this.getUsers(params))
        .catch(this.$defaultHandlerHttp);
    }
  };
</script>

<style lang="less" scoped>
  @import '../../style/variables.less';
  @import '../../style/form.less';

  .loadingContainer {
    p {
      margin: auto;
    }
  }

  .v-card {
    margin-bottom: 20px;
  }

  h3 {
    text-align: left;
  }

  .userCard {
    border: 2px solid;
  }

  .noChanges {
    margin-top: 25px;
    text-align: center;
    display: block;
  }
</style>
